import { App, Skeleton } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  broadcastApi,
  useBroadcasts,
  useSubsSummary,
  useSubscribers,
  type Broadcast,
} from '@entities/broadcast';
import { useChannelCards } from '@entities/channel';
import { extractErrorMessage } from '@shared/api';
import { IconPlus, num, shortWhen, TopBar } from '@shared/ui/v2';
import { BcStatus, whenFull } from './bcUi';
import './broadcasts.css';

function Delivery({ b }: { b: Broadcast }) {
  if (b.status === 'draft') return <span className="v2-muted">не отправлена</span>;
  if (b.status === 'queued')
    return <span className="v2-muted">в очереди · {num(b.total_count)}</span>;
  if (b.status === 'sending') {
    const pct = b.total_count
      ? Math.round((100 * (b.sent_count + b.failed_count)) / b.total_count)
      : 0;
    return (
      <div className="bc-deliv">
        <span>
          <b>{num(b.sent_count + b.failed_count)}</b> из {num(b.total_count)}
        </span>
        <span className="bc-bar">
          <i style={{ width: `${pct}%` }} />
        </span>
      </div>
    );
  }
  if (b.status === 'cancelled')
    return (
      <span>
        <b>{num(b.sent_count)}</b> из {num(b.total_count)}, остановлена
      </span>
    );
  if (b.status === 'failed') return <span style={{ color: '#B91C1C' }}>не отправлена</span>;
  return (
    <span>
      <b>{num(b.sent_count)}</b> доставлено
      {b.failed_count > 0 && <span className="bc-fail"> · {num(b.failed_count)} не дошло</span>}
    </span>
  );
}

/** Дата подписки: «сегодня» / «вчера» / «26 окт». */
function dayOnly(iso: string) {
  const s = shortWhen(iso);
  return /^\d\d:\d\d$/.test(s) ? 'сегодня' : s;
}

function useDebounced<T>(v: T, ms: number) {
  const [d, setD] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setD(v), ms);
    return () => clearTimeout(t);
  }, [v, ms]);
  return d;
}

function Subscribers({ aid }: { aid: string }) {
  const { data: s } = useSubsSummary(aid);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const qd = useDebounced(q.trim(), 350);
  const list = useSubscribers(aid, qd, open);
  const items = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);
  const total = list.data?.pages[0]?.total ?? 0;
  const sentinel = useRef<HTMLDivElement>(null);
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = list;
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && hasNextPage && !isFetchingNextPage) fetchNextPage();
    });
    io.observe(el);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, items.length]);

  return (
    <section className="v2-card bc-subs" aria-label="Подписчики">
      <div className="bc-subs-row">
        <span className="bc-stat">
          <b>{num(s?.total)}</b> подписчиков
        </span>
        <span className="bc-sep" />
        <span className="bc-stat">
          <b>+{num(s?.new_7d)}</b> новых за 7 дней
        </span>
        <span className="bc-sep" />
        <span className="bc-stat">
          <b>{num(s?.active_30d)}</b> писали за месяц
        </span>
        <span className="v2-xs v2-muted">
          Telegram {num(s?.telegram)} · MAX {num(s?.max)}
        </span>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          className="bc-toggle"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          Список подписчиков {open ? '▴' : '▾'}
        </button>
      </div>
      {open && (
        <div className="bc-subs-list">
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <label htmlFor="bc-q" className="v2-sr">
              Поиск подписчика
            </label>
            <input
              id="bc-q"
              className="st-input"
              style={{ maxWidth: 320 }}
              placeholder="Поиск по имени или логину"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <span className="v2-xs v2-muted">
              Подписчик — тот, кто написал боту. Кто заблокировал бота, рассылки не получает.
            </span>
          </div>
          <div className="v2-table-wrap" style={{ maxHeight: 420, overflowY: 'auto' }}>
            <table className="v2-table bc-table">
              <thead>
                <tr>
                  <th scope="col">Подписчик</th>
                  <th scope="col">Канал</th>
                  <th scope="col">Подписался</th>
                  <th scope="col" style={{ textAlign: 'right' }}>
                    Сообщений
                  </th>
                  <th scope="col">Последний раз писал</th>
                </tr>
              </thead>
              <tbody>
                {items.map((u) => (
                  <tr key={u.id}>
                    <td style={{ fontWeight: 600 }}>{u.name}</td>
                    <td>{u.channel_title}</td>
                    <td>{dayOnly(u.subscribed_at)}</td>
                    <td style={{ textAlign: 'right' }}>{num(u.messages)}</td>
                    <td>{u.last_at ? shortWhen(u.last_at) : '—'}</td>
                  </tr>
                ))}
                {!list.isLoading && !items.length && (
                  <tr>
                    <td colSpan={5} className="v2-muted">
                      {qd
                        ? 'Никого не нашли.'
                        : 'Подписчиков пока нет — им станет каждый, кто напишет боту.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <div ref={sentinel} style={{ height: 1 }} />
          </div>
          {total > items.length && (
            <div className="v2-xs v2-muted">
              Показаны {num(items.length)} из {num(total)} · при прокрутке подгрузим ещё
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/** Экран 9 «Ассистент → Рассылки» (утверждён 28.10.2026). */
export function BroadcastsPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { data, isLoading, error } = useBroadcasts(id);
  const { data: channels } = useChannelCards(id);
  const bots = (channels ?? []).filter((c) => c.platform !== 'widget');
  const [creating, setCreating] = useState(false);
  const byId = new Map((channels ?? []).map((c) => [c.id, c]));

  const create = async () => {
    if (!bots.length) {
      message.info('Сначала подключите бота в Telegram или MAX — рассылки уходят его подписчикам');
      navigate(`/assistants/${id}/channels/new?type=telegram`);
      return;
    }
    setCreating(true);
    try {
      const b = await broadcastApi.create(id, {
        channel_id: (bots.find((c) => c.status === 'active') ?? bots[0]).id,
        title: 'Новая рассылка',
        body_text: '',
      });
      navigate(`/assistants/${id}/broadcasts/${b.id}`);
    } catch (e) {
      message.error(extractErrorMessage(e));
    } finally {
      setCreating(false);
    }
  };

  const list = data ?? [];

  return (
    <>
      <TopBar
        section
        title="Рассылки"
        hint="Сообщения подписчикам ваших ботов в Telegram и MAX"
        actions={
          <button type="button" className="v2-btn is-primary" disabled={creating} onClick={create}>
            <IconPlus size={15} />
            Новая рассылка
          </button>
        }
      />
      <div
        className="v2-content is-section"
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <Subscribers aid={id} />
        {isLoading && <Skeleton active paragraph={{ rows: 4 }} />}
        {!!error && (
          <div className="v2-card v2-card-pad">
            Не удалось загрузить рассылки: {extractErrorMessage(error)}
          </div>
        )}
        {data && (
          <section className="v2-card" aria-label="Рассылки" style={{ overflow: 'hidden' }}>
            {!list.length ? (
              <div className="v2-card-pad v2-small v2-muted">
                Рассылок пока нет. Нажмите «Новая рассылка» — напишите сообщение, проверьте его на
                себе и отправьте всем подписчикам бота.
              </div>
            ) : (
              <div className="v2-table-wrap">
                <table className="v2-table bc-table bc-list">
                  <thead>
                    <tr>
                      <th scope="col">Рассылка</th>
                      <th scope="col">Куда</th>
                      <th scope="col">Статус</th>
                      <th scope="col">Когда</th>
                      <th scope="col">Доставка</th>
                      <th aria-hidden="true" />
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((b) => {
                      const ch = byId.get(b.channel_id);
                      return (
                        <tr
                          key={b.id}
                          className="bc-row"
                          onClick={() => navigate(`/assistants/${id}/broadcasts/${b.id}`)}
                        >
                          <td className="bc-title">
                            <Link
                              to={`/assistants/${id}/broadcasts/${b.id}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {b.title || 'Без названия'}
                            </Link>
                          </td>
                          <td>
                            {ch
                              ? ch.platform === 'max'
                                ? `MAX · ${ch.bot_display_name || ch.title}`
                                : ch.title
                              : '—'}
                          </td>
                          <td>
                            <BcStatus status={b.status} />
                          </td>
                          <td className="v2-muted">
                            {b.started_at ? whenFull(b.started_at) : '—'}
                          </td>
                          <td>
                            <Delivery b={b} />
                          </td>
                          <td style={{ width: 24, color: '#9CA3AF' }}>›</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
      </div>
    </>
  );
}
